// What the person screens know about a person's profile fields, in one place: the order and
// grouping of PersonField, how each is edited and shown, and the labels (personAdmin.field.<key>).
// The keys are the backend's (PersonField.key): the column name in camelCase.
import { INTEREST_CATEGORIES, STATUSES } from '../../constants/doctorProfile';

// Professional status codes allowed on a person (ProfessionalStatus and the CHECK of V47; the same
// list the frontend canonical constants/doctorProfile.STATUSES holds). SPEAKER is one of them again
// (owner decision 2026-10-14) and grants nothing: speaker access is computed from the invitations a
// person holds (D11, authority.AuthorityService).
export const PROFESSIONAL_STATUS_VALUES = STATUSES;

export const INTEREST_VALUES = INTEREST_CATEGORIES.flatMap((category) => category.interests.map((interest) => ({
  interest, categoryId: category.labelId, categoryDefault: category.labelDefault,
})));

// Every profile field, in PersonField order (the backend sends them in that order too).
export const PERSON_FIELDS = [
  { key: 'title', group: 'identity', kind: 'text' },
  { key: 'firstName', group: 'identity', kind: 'text' },
  { key: 'lastName', group: 'identity', kind: 'text' },
  { key: 'titleSuffix', group: 'identity', kind: 'text' },
  { key: 'contactEmail', group: 'identity', kind: 'text' },
  { key: 'phone', group: 'contact', kind: 'text' },
  { key: 'streetAddress', group: 'contact', kind: 'text' },
  { key: 'postalCode', group: 'contact', kind: 'text' },
  { key: 'city', group: 'contact', kind: 'text' },
  { key: 'state', group: 'contact', kind: 'text' },
  { key: 'country', group: 'contact', kind: 'text' },
  { key: 'institution', group: 'work', kind: 'text' },
  { key: 'specialty', group: 'work', kind: 'text' },
  { key: 'position', group: 'work', kind: 'text' },
  { key: 'professionalStatuses', group: 'classification', kind: 'statuses' },
  { key: 'interests', group: 'classification', kind: 'interests' },
  { key: 'bioDe', group: 'bio', kind: 'textarea' },
  { key: 'bioEn', group: 'bio', kind: 'textarea' },
  { key: 'motivationDe', group: 'bio', kind: 'textarea' },
  { key: 'motivationEn', group: 'bio', kind: 'textarea' },
  { key: 'photoUrl', group: 'links', kind: 'text' },
  { key: 'linkedinUrl', group: 'links', kind: 'text' },
  { key: 'websiteUrl', group: 'links', kind: 'text' },
  { key: 'instagramUrl', group: 'links', kind: 'text' },
  { key: 'facebookUrl', group: 'links', kind: 'text' },
  { key: 'internalNotes', group: 'admin', kind: 'textarea' },
];

// The groups, in the order they are shown; `personAdmin.group.<id>` labels them.
export const FIELD_GROUPS = ['identity', 'contact', 'work', 'classification', 'bio', 'links', 'admin']
  .map((id) => ({ id, fields: PERSON_FIELDS.filter((field) => field.group === id) }))
  .filter((group) => group.fields.length > 0);

// D33: the only fields an admin edits on a person with an account; everyone edits their own profile
// and reaches the rest of the person through an invitation.
export const ADMIN_OWNED_FIELDS = ['internalNotes', 'position'];
export const isAdminOwned = (key) => ADMIN_OWNED_FIELDS.includes(key);

// What the edit form offers: everything on a person without an account, only the admin-owned fields
// on an account holder. With "Apply directly" (force) everything but contactEmail, which is an
// account holder's login (D34) and never forced.
export const editableFields = (hasAccount, force = false) => PERSON_FIELDS.filter((field) => !hasAccount
  || isAdminOwned(field.key) || (force && field.key !== 'contactEmail'));

// The edit form's starting values, or the payload's; text stays a string, lists an array.
export const fieldValue = (field, value) => {
  if (field.kind === 'statuses' || field.kind === 'interests') return Array.isArray(value) ? value : [];
  return value == null ? '' : String(value);
};

export const formStateOf = (fields = {}) => Object.fromEntries(PERSON_FIELDS.map((field) => {
  const given = fields[field.key];
  return [field.key, fieldValue(field, given ? given.value : undefined)];
}));

// Only the fields the admin actually changed: blank text clears a field (the backend normalises a
// blank to null), a list is sent as the array of values. A field left as it was is not sent, so the
// change set holds only real edits.
export const changedPayload = (form, current) => {
  const payload = {};
  for (const field of PERSON_FIELDS) {
    const now = form[field.key];
    const then = fieldValue(field, current[field.key]?.value);
    if (field.kind === 'statuses' || field.kind === 'interests') {
      const a = [...(now || [])].sort();
      const b = [...(then || [])].sort();
      if (a.length !== b.length || a.some((value, i) => value !== b[i])) payload[field.key] = now || [];
    } else {
      const a = (now == null ? '' : String(now)).trim();
      const b = (then == null ? '' : String(then)).trim();
      if (a !== b) payload[field.key] = a === '' ? null : a;
    }
  }
  return payload;
};

// The event name in the site language, for the list's event filter.
export const eventTitle = (language, nameDe, nameEn) => (language === 'en' ? (nameEn || nameDe) : (nameDe || nameEn));

// What went wrong, as the backend says it: the admin API answers a plain-text body (400 a bad body
// or unknown field, 409 refused by a rule), so that text is the message the screen shows.
export const errorText = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === 'string' && data) return data;
  if (data && typeof data.message === 'string' && data.message) return data.message;
  if (err?.message) return err.message;
  return fallback;
};

// The labels for the array codes the backend stores (constants/doctorProfile).
export const statusLabel = (t, status) => t(`speaker.role.${status}`, status);
export const interestLabel = (t, interest) => t(`speaker.interest.${interest}`, interest);
export const originLabel = (t, origin) => t(`personAdmin.origin.${origin}`, origin);
